import React, { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { AppUser } from '../types/auth';
import { apiFetch, refreshCsrfToken } from '../services/api';
import { createGoogleProvider, getFirebaseAuth } from '../firebase/config';
import { onAuthStateChanged, signInWithPopup, signOut } from 'firebase/auth';

interface AuthResponse { user: AppUser; }
interface AuthContextProps {
  currentUser: AppUser | null; loading: boolean;
  signUp: (email: string, password: string, displayName: string, role?: 'individual' | 'homeowner') => Promise<AppUser>;
  signIn: (email: string, password: string) => Promise<AppUser>;
  signInWithGoogle: () => Promise<AppUser>; selectAccountType: (role: 'individual' | 'homeowner') => Promise<AppUser>; logOut: () => Promise<void>;
  resetPassword: (email: string) => Promise<void>; updateUserProfile: (displayName: string, photoURL?: string) => Promise<void>;
}
const AuthContext = createContext<AuthContextProps | null>(null);
export function useAuth() { const context = useContext(AuthContext); if (!context) throw new Error('useAuth must be used within an AuthProvider'); return context; }
export function AuthProvider({ children }: { children: ReactNode }) {
  const [currentUser, setCurrentUser] = useState<AppUser | null>(null); const [loading, setLoading] = useState(true);
  // The access/refresh tokens themselves live only in httpOnly cookies set by
  // the server; the client never reads or stores them, so "session state" here
  // is just the resolved user returned by the API.
  const saveSession = (response: AuthResponse) => { setCurrentUser(response.user); return response.user; };
  useEffect(() => {
    let unsubscribe: (() => void) | undefined;
    const restore = async () => {
      const fetchMe = async (displayName: string | null, photoURL: string | null, hasFirebaseSession: boolean, hasCookieSession: boolean) => {
        if (!hasCookieSession && !hasFirebaseSession) {
          setCurrentUser(null);
          setLoading(false);
          return;
        }
        try {
          const result = await apiFetch<{ data: { id: string; email: string; role: AppUser['role']; needsAccountTypeSelection?: boolean } }>('/auth/me');
          setCurrentUser({ uid: result.data.id, email: result.data.email, displayName, photoURL, role: result.data.role, needsAccountTypeSelection: result.data.needsAccountTypeSelection });
        } catch {
          setCurrentUser(null);
        } finally {
          setLoading(false);
        }
      };
      try {
        // Wait for Firebase to resolve any persisted Google session before
        // deciding there is no user: otherwise a fresh page load would race
        // ahead of Firebase's own (async) session restoration.
        const auth = await getFirebaseAuth();
        unsubscribe = onAuthStateChanged(auth, (fbUser) => {
          refreshCsrfToken()
            .catch(() => false)
            .then((hasCookieSession) => fetchMe(fbUser?.displayName || null, fbUser?.photoURL || null, Boolean(fbUser), hasCookieSession));
        });
      } catch {
        // Google sign-in isn't configured; fall back to a cookie-only session.
        refreshCsrfToken()
          .catch(() => false)
          .then((hasCookieSession) => fetchMe(null, null, false, hasCookieSession));
      }
    };
    restore();
    return () => unsubscribe?.();
  }, []);
  const signUp = async (email: string, password: string, displayName: string, role: 'individual' | 'homeowner' = 'individual') => { const [firstName = '', ...rest] = displayName.trim().split(/\s+/); await refreshCsrfToken(); const response = await apiFetch<AuthResponse>('/auth/signup', { method: 'POST', body: JSON.stringify({ email, password, firstName, lastName: rest.join(' '), role }) }); return saveSession(response); };
  const signIn = async (email: string, password: string) => { await refreshCsrfToken(); return saveSession(await apiFetch<AuthResponse>('/auth/signin', { method: 'POST', body: JSON.stringify({ email, password }) })); };
  const finishFirebaseSignIn = async (displayName: string | null, photoURL: string | null): Promise<AppUser> => {
    const result = await apiFetch<{ data: { id: string; email: string; role: AppUser['role']; needsAccountTypeSelection?: boolean } }>('/auth/me');
    const user = { uid: result.data.id, email: result.data.email, displayName, photoURL, role: result.data.role, needsAccountTypeSelection: result.data.needsAccountTypeSelection };
    setCurrentUser(user);
    return user;
  };
  const signInWithGoogle = async (): Promise<AppUser> => {
    const result = await signInWithPopup(await getFirebaseAuth(), createGoogleProvider());
    return finishFirebaseSignIn(result.user.displayName, result.user.photoURL);
  };
  const selectAccountType = async (role: 'individual' | 'homeowner'): Promise<AppUser> => {
    await apiFetch('/profiles/me/account-type', { method: 'PUT', body: JSON.stringify({ role }) });
    if (!currentUser) throw new Error('You must be signed in to select an account type.');
    const selectedUser = { ...currentUser, role, needsAccountTypeSelection: false };
    setCurrentUser(selectedUser);
    return selectedUser;
  };
  const logOut = async () => {
    try { await apiFetch('/auth/logout', { method: 'POST' }); } finally {
      try { await signOut(await getFirebaseAuth()); } catch { /* Google sign-in not configured or no active Firebase session */ }
      setCurrentUser(null);
    }
  };
  const resetPassword = async (email: string) => { await refreshCsrfToken(); await apiFetch('/auth/password-reset', { method: 'POST', body: JSON.stringify({ email }) }); };
  const updateUserProfile = async (displayName: string, photoURL?: string) => { setCurrentUser((user) => user ? { ...user, displayName, photoURL: photoURL || user.photoURL } : user); };
  return <AuthContext.Provider value={{ currentUser, loading, signUp, signIn, signInWithGoogle, selectAccountType, logOut, resetPassword, updateUserProfile }}>{!loading && children}</AuthContext.Provider>;
}
